/**
 * @file Map.tsx
 * @description
 * Primary map visualization component.
 *
 * Responsibilities:
 * - Renders congressional district polygons and optional point markers.
 * - Wires D3 projection, zoom, and pan behavior.
 * - Connects to global data/viz stores for coloring and hover state.
 * - Supports both transient hover tooltips and click-to-lock selection on polygons.
 *   When a polygon is clicked, its tooltip remains visible until the user clicks
 *   the same polygon again or clicks on the map background to clear the selection.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import * as d3 from 'd3';

import { useMapData } from '@/hooks/useMapData';
import { useMetricColorScale } from '@/hooks/useMetricColorScale';
import { useVoteColor } from '@/hooks/useVoteColor';
import { PolygonsLayer } from '@/components/map/PolygonsLayer';
import { PointsLayer } from '@/components/map/PointsLayer';
import { LegendsLayer } from '@/components/map/LegendsLayer';

import { useDataStore, useVizStore } from '@/store';

import { type DistrictId } from '@/types.ts';

const WIDTH = 980;
const HEIGHT = 620;
// Margins to reserve space for legend (bottom only)
const MARGIN = { top: 10, right: 10, bottom: 60, left: 10 };

export default function Map() {
  // Geometry data for the map
  const { polys, points } = useMapData();

  const svgRef = useRef<SVGSVGElement | null>(null);
  const zoomGroupRef = useRef<SVGGElement | null>(null);
  // Track current zoom scale (1 == no zoom). Shared in state to be used by other child components (i.e. points)
  const [zoomScale, setZoomScale] = useState(1);

  const districts = useDataStore((s) => s.districts);

  const polyColor = useVizStore((s) => s.polyColor);
  const pointsColor = useVizStore((s) => s.pointsColor);
  const showPoints = useVizStore((s) => s.showPoints);
  const voteNoDataColor = useVizStore((s) => s.voteNoDataColor);
  const selectedDistrictId = useVizStore((s) => s.selectedDistrictId);
  const selectedDistricts = useVizStore((s) => s.selectedDistricts);
  const setHoverFromMap = useVizStore((s) => s.setHoverFromMap);
  const setSelectedDistrictFromMap = useVizStore((s) => s.setSelectedDistrictFromMap);
  const clearHover = useVizStore((s) => s.clearHover);
  const hoveredDistricts = useVizStore((s) => s.hoveredDistricts);
  const hoverSource = useVizStore((s) => s.hoverSource);

  // Create color scales for metric mode using centralized hook
  const polyMetricScaleData = useMetricColorScale(polyColor === 'metric');
  const pointsMetricScaleData = useMetricColorScale(pointsColor === 'metric');

  // Get vote color function for vote type mode
  const getVoteColor = useVoteColor();

  /**
   * Color function for polygons.
   * Returns either metric-based color or vote type color based on `polyColor` setting.
   *
   * @param id - District ID in format "congress-state-district" (e.g., "118-ca-29")
   * @returns Color string for the district polygon
   */
  const fillForId = useMemo(() => {
    return (id: string) => {
      const district = districts[id as DistrictId];

      if (polyColor === 'metric') {
        if (
          !polyMetricScaleData.scale ||
          district?.metricValue == null ||
          isNaN(district.metricValue)
        ) {
          return voteNoDataColor || '#CCCCCC';
        }
        return polyMetricScaleData.scale(district.metricValue);
      } else {
        // voteType mode - use centralized vote color hook
        return getVoteColor(id);
      }
    };
  }, [districts, polyColor, polyMetricScaleData, voteNoDataColor, getVoteColor]);

  /**
   * Color function for points.
   * Returns either metric-based color or vote type color based on `pointsColor` setting.
   *
   * @param id - District ID in format "congress-state-district" (e.g., "118-ca-29")
   * @returns Color string for the district point
   */
  const fillForPointId = useMemo(() => {
    return (id: string) => {
      const district = districts[id as DistrictId];

      if (pointsColor === 'metric') {
        if (
          !pointsMetricScaleData.scale ||
          district?.metricValue == null ||
          isNaN(district.metricValue)
        ) {
          return voteNoDataColor || '#CCCCCC';
        }
        return pointsMetricScaleData.scale(district.metricValue);
      } else {
        // voteType mode - use centralized vote color hook
        return getVoteColor(id);
      }
    };
  }, [districts, pointsColor, pointsMetricScaleData, voteNoDataColor, getVoteColor]);

  /** D3 projection + path generator fit to the geometry */
  const { path, projection } = useMemo(() => {
    const proj = d3.geoAlbersUsa();
    const pathGen = d3.geoPath(proj);
    if (polys) {
      // Use fitExtent to reserve space for legend (gutter)
      const mapWidth = WIDTH - MARGIN.left - MARGIN.right;
      const mapHeight = HEIGHT - MARGIN.top - MARGIN.bottom;
      proj.fitExtent(
        [
          [MARGIN.left, MARGIN.top],
          [MARGIN.left + mapWidth, MARGIN.top + mapHeight],
        ],
        polys,
      );
    }
    return { path: pathGen, projection: proj };
  }, [polys]);

  /** Zoom + pan behavior */
  useEffect(() => {
    if (!svgRef.current || !zoomGroupRef.current) return;

    const svg = d3.select<SVGSVGElement, unknown>(svgRef.current);
    const g = d3.select<SVGGElement, unknown>(zoomGroupRef.current);

    const zoom = d3
      .zoom<SVGSVGElement, unknown>()
      .scaleExtent([1, 8])
      .on('start', () => svg.style('cursor', 'grabbing'))
      .on('end', () => svg.style('cursor', 'default'))
      .on('zoom', (event: d3.D3ZoomEvent<SVGSVGElement, unknown>) => {
        const transform: d3.ZoomTransform = event.transform;
        g.attr('transform', transform.toString());
        // Track current zoom scale so child layers can react (e.g. adjust point size)
        setZoomScale(transform.k);
      });

    svg.call(zoom);

    // Custom double-click to reset view
    svg.on('dblclick.zoom', null);
    svg.on('dblclick', () => {
      svg
        .transition()
        .duration(250)
        .call((transition: d3.Transition<SVGSVGElement, unknown, null, undefined>) => {
          zoom.transform(
            transition as unknown as d3.Selection<SVGSVGElement, unknown, null, undefined>,
            d3.zoomIdentity,
          );
        });
    });
  }, []);

  /** Hover handlers
   *
   * Hover is ignored while a polygon selection is locked via click so that
   * moving the mouse over other districts does not change the tooltip.
   */
  const handlePolygonHover = (id: string, e: React.MouseEvent<SVGPathElement, MouseEvent>) => {
    if (selectedDistrictId || selectedDistricts.length > 0) return;
    const bounds = (e.target as SVGPathElement).getBoundingClientRect();
    const x = bounds.x + bounds.width / 2;
    const y = bounds.y;
    setHoverFromMap(id, { x, y });
  };
  const handlePolygonLeave = () => {
    if (selectedDistrictId || selectedDistricts.length > 0) return;
    // Only clear if the hover source is 'map' (not 'plot')
    if (hoverSource === 'map') {
      clearHover();
    }
  };

  const handlePointHover = (id: string, e: React.MouseEvent<SVGCircleElement, MouseEvent>) => {
    // If any districts are selected, don't update hover state or tooltip position
    // This prevents the tooltip from moving when the mouse moves over points
    if (selectedDistrictId || selectedDistricts.length > 0) return;
    const bounds = (e.target as SVGCircleElement).getBoundingClientRect();
    const x = bounds.x + bounds.width / 2;
    const y = bounds.y;
    setHoverFromMap(id, { x, y });
  };
  const handlePointLeave = () => {
    // Don't clear hover if a selection is locked
    if (selectedDistrictId || selectedDistricts.length > 0) return;
    // Only clear if the hover source is 'map' (not 'plot')
    if (hoverSource === 'map') {
      clearHover();
    }
  };

  /**
   * Click handler for polygons.
   *
   * - Clicking a polygon locks selection and tooltip position for that district.
   * - Event propagation is stopped so background click-to-clear does not fire.
   */
  const handlePolygonClick = (id: string, e: React.MouseEvent<SVGPathElement, MouseEvent>) => {
    e.stopPropagation();
    const bounds = (e.target as SVGPathElement).getBoundingClientRect();
    const x = bounds.x + bounds.width / 2;
    const y = bounds.y;

    setSelectedDistrictFromMap(id, { x, y });
  };

  // Determine which IDs to highlight:
  // - When districts are explicitly selected via click, highlight those IDs.
  // - Otherwise, use the transient hoveredDistricts for 'plot' and 'map' sources.
  const highlightedIds = useMemo(() => {
    if (selectedDistrictId) {
      return [selectedDistrictId];
    }
    if (selectedDistricts.length > 0) {
      return selectedDistricts;
    }
    return hoveredDistricts;
  }, [selectedDistrictId, selectedDistricts, hoveredDistricts]);

  // Determine which point IDs should be visible
  // When hoverSource is 'plot', only show points for focused polygons (hoveredDistricts)
  // Otherwise, show all points (when showPoints is true)
  const visiblePointIds = useMemo(() => {
    if (hoverSource === 'plot' && hoveredDistricts.length > 0) {
      return hoveredDistricts;
    }
    // Return undefined to show all points when not filtering
    return undefined;
  }, [hoverSource, hoveredDistricts]);

  return (
    <div
      className="relative h-100"
      style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}
    >
      <svg
        ref={svgRef}
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        role="img"
        aria-label="US congressional districts colored by bill vote"
        style={{ width: '100%', height: '100%', display: 'block', flex: '1 1 auto' }}
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <filter id="hover-glow" x="-50%" y="-50%" width="200%" height="200%">
            <feDropShadow dx="0" dy="0" stdDeviation="4" floodColor="#111111" floodOpacity="0.6" />
          </filter>
        </defs>
        {/* Zoomable group: actual map layers */}
        <g ref={zoomGroupRef}>
          <PolygonsLayer
            polys={polys}
            path={path}
            fillForId={fillForId}
            hoveredIds={highlightedIds}
            dimInactive={Boolean(highlightedIds.length > 0)}
            onHover={handlePolygonHover}
            onLeave={handlePolygonLeave}
            onClick={handlePolygonClick}
          />
          <PointsLayer
            points={points}
            projection={projection}
            fillForId={fillForPointId}
            visible={showPoints ?? false}
            visibleIds={visiblePointIds}
            zoomScale={zoomScale}
            onHover={handlePointHover}
            onLeave={handlePointLeave}
          />
        </g>

        {/* Legends */}
        <LegendsLayer width={WIDTH} height={HEIGHT} />
      </svg>
    </div>
  );
}
